import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Truck, Package, CheckCircle2, UserCheck, Boxes } from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import FilterBar from "@/components/FilterBar";
import { StatusPill } from "@/components/Pills";
import { useLang } from "@/context/LanguageContext";

// Phase B (Feb 2026): Full-screen drilldown for Manager.
// Replaces the old <UnitsDrilldown /> modal.
// Variants:
//   /manager/orders             → all today's orders
//   /manager/orders/assigned    → status in [assigned, out_for_delivery, delivered]
//   /manager/orders/delivered   → status = delivered
//   /manager/orders/pending     → status in [pending, assigned, out_for_delivery]
const VARIANT_CONFIG = {
  all: {
    titleKey: "orders",
    icon: Truck,
    color: "bg-[#0055FF]",
    fetchParams: () => ({}),
    filterFn: (o) => true,
  },
  assigned: {
    titleKey: "assigned",
    icon: UserCheck,
    color: "bg-[#5856D6]",
    fetchParams: () => ({}),
    filterFn: (o) => ["assigned", "out_for_delivery", "delivered"].includes(o.status),
  },
  delivered: {
    titleKey: "delivered_today",
    icon: CheckCircle2,
    color: "bg-[#34C759]",
    fetchParams: () => ({ status: "delivered" }),
    filterFn: (o) => o.status === "delivered",
  },
  pending: {
    titleKey: "pending",
    icon: Package,
    color: "bg-[#FF9500]",
    fetchParams: () => ({}),
    filterFn: (o) => ["pending", "assigned", "out_for_delivery"].includes(o.status),
  },
};

export default function ManagerOrdersList() {
  const nav = useNavigate();
  const { variant = "all" } = useParams();
  const cfg = VARIANT_CONFIG[variant] || VARIANT_CONFIG.all;
  const { t, displayName } = useLang();
  const [orders, setOrders] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", salesman_id: "", start_date: "", end_date: "" });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters, variant]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        ...cfg.fetchParams(),
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)),
      };
      const [o, s] = await Promise.all([
        api.get("/orders", { params }),
        salesmen.length === 0
          ? api.get("/users", { params: { role: "salesman" } })
          : Promise.resolve({ data: salesmen }),
      ]);
      setOrders((o.data || []).filter(cfg.filterFn));
      if (salesmen.length === 0) setSalesmen(s.data || []);
    } finally { setLoading(false); }
  };

  // Sort by total_units (boxes) descending — highest boxes first
  const sortedOrders = [...orders].sort((a, b) => (b.total_units || 0) - (a.total_units || 0));
  const totalBoxes = sortedOrders.reduce((s, o) => s + (o.total_units || 0), 0);

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-10 page-slide-in">
      <TopBar title={t(cfg.titleKey)} onBack={() => nav("/manager")} />
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5 fade-up">
        {/* Summary banner */}
        <div className={`${cfg.color} text-white rounded-3xl p-5 shadow-lg`} data-testid="ml-summary">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/15 grid place-items-center">
              <cfg.icon className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] opacity-80 font-bold">{t(cfg.titleKey)}</div>
              <div className="heading-font text-3xl font-bold">{sortedOrders.length}</div>
              <div className="text-xs opacity-80 mt-0.5 flex items-center gap-1">
                <Boxes className="w-3.5 h-3.5" /> {totalBoxes} {t("boxes")}
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 soft-shadow">
          <FilterBar
            fields={[
              { key: "q", type: "text", label: "Search shop name…" },
              { key: "salesman_id", type: "select", label: "Salesman", options: salesmen.map(s => ({value: s.id, label: s.name})) },
              { key: "start_date", type: "date", label: "From" },
              { key: "end_date", type: "date", label: "To" },
            ]}
            values={filters}
            onChange={setFilters}
            onClear={() => setFilters({ q: "", salesman_id: "", start_date: "", end_date: "" })}
          />
        </div>

        {/* Orders list */}
        <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-[#86868B]">{t("loading")}</div>
          ) : sortedOrders.length === 0 ? (
            <div className="p-12 text-center text-[#86868B]" data-testid="ml-empty">No orders</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {sortedOrders.map((o, idx) => (
                <div key={o.id} className="p-4 sm:p-5 hover:bg-gray-50/40" data-testid={`ml-row-${o.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-[#86868B] tabular-nums">#{idx + 1}</span>
                        <div className="heading-font font-bold text-[#1D1D1F]">{o.shop_name}</div>
                        <StatusPill status={o.status} t={t} />
                        {o.priority && <span className="pill bg-red-50 text-red-700">⚡ {t("high_priority")}</span>}
                      </div>
                      <div className="text-xs text-[#86868B] mt-1 flex flex-wrap gap-2">
                        {o.assigned_salesman_name && <span>→ {o.assigned_salesman_name}</span>}
                        <span>{new Date(o.created_at).toLocaleString([], {month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'})}</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="heading-font text-2xl font-bold text-[#1D1D1F]">{o.total_units || 0}</div>
                      <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{t("boxes")}</div>
                    </div>
                  </div>
                  {o.items && o.items.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {o.items.slice(0, 5).map((it, i) => (
                        <span key={i} className="pill bg-gray-100 text-gray-700 text-[11px]">
                          {displayName(it)} ×{it.quantity}
                        </span>
                      ))}
                      {o.items.length > 5 && (
                        <span className="pill bg-gray-100 text-gray-700 text-[11px]">+{o.items.length - 5}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
